import {useMemo} from 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {JBreakdownPage} from '@/pages/components/audit-program/j-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface JBreakdownViewProps {
    header: AuditProgramSectionVO;
    page: JBreakdownPage;
    refCode: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.75rem', py: 0.75, px: 0.5};
/** Contain InputField default minWidth:110 so adjacent cells don't overlap */
const bodySx = {...cellBorder, py: 0.45, px: 0.4, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** J_1 / J_2 — receivable or prepayment amount + breakdown lines. */
export default function JBreakdownView({
    header,
    page,
    refCode,
    values,
    onChange,
}: JBreakdownViewProps) {
    const rowIds = useMemo(
        () => Array.from({length: Math.max(1, page.blankBreakdownRows || 8)}, (_, i) => `b${i + 1}`),
        [page.blankBreakdownRows],
    );

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 0.5, mt: 0.5}}
            >
                {page.title}
            </Typography>
            {page.subtitle ? (
                <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', mb: 1.5}}>
                    {page.subtitle}
                </Typography>
            ) : (
                <Box sx={{mb: 1.5}}/>
            )}

            <TableContainer sx={{mb: 2, maxWidth: 640}}>
                <Table size="small" sx={{tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col/>
                        <col style={{width: 48}}/>
                        <col style={{width: 140}}/>
                        <col style={{width: 140}}/>
                        <col style={{width: 48}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="right">{page.cyLabel}</TableCell>
                            <TableCell sx={headSx} align="right">{page.lyLabel}</TableCell>
                            <TableCell sx={headSx}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={bodySx}>
                                <Typography variant="body2">{page.amountLabel}</Typography>
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="center"
                                    sx={fieldSx}
                                    value={values['amount.tick'] ?? ''}
                                    onChange={(e) => onChange('amount.tick', e.target.value)}
                                />
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="right"
                                    sx={fieldSx}
                                    value={values['amount.cy'] ?? ''}
                                    onChange={(e) => onChange('amount.cy', e.target.value)}
                                />
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="right"
                                    sx={fieldSx}
                                    value={values['amount.ly'] ?? ''}
                                    onChange={(e) => onChange('amount.ly', e.target.value)}
                                />
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="center"
                                    sx={fieldSx}
                                    value={values['amount.lyTick'] ?? ''}
                                    onChange={(e) => onChange('amount.lyTick', e.target.value)}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 1}}>
                {page.breakdownTitle}
            </Typography>
            <TableContainer sx={{mb: 2, overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 860, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 40}}/>
                        <col style={{width: 110}}/>
                        <col style={{width: 160}}/>
                        <col/>
                        <col style={{width: 100}}/>
                        <col style={{width: 120}}/>
                        <col style={{width: 48}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx}>Date</TableCell>
                            <TableCell sx={headSx}>Description</TableCell>
                            <TableCell sx={headSx}>Details</TableCell>
                            <TableCell sx={headSx}>Cheque No.</TableCell>
                            <TableCell sx={headSx} align="right">Amount</TableCell>
                            <TableCell sx={headSx}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((id, idx) => (
                            <TableRow key={id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" color="text.secondary">{idx + 1}</Typography>
                                </TableCell>
                                {(['date', 'description', 'details', 'cheque', 'amount', 'tick'] as const).map((field) => (
                                    <TableCell key={field} sx={bodySx}>
                                        <InputField
                                            type="text"
                                            fullWidth
                                            compact
                                            align={field === 'amount' || field === 'tick' ? 'right' : 'left'}
                                            sx={fieldSx}
                                            value={values[`${id}.${field}`] ?? ''}
                                            onChange={(e) => onChange(`${id}.${field}`, e.target.value)}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell sx={bodySx} colSpan={5} align="right">
                                <Typography variant="body2" fontWeight={600}>Total</Typography>
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="right"
                                    sx={fieldSx}
                                    value={values['breakdown.total'] ?? ''}
                                    onChange={(e) => onChange('breakdown.total', e.target.value)}
                                />
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="center"
                                    sx={fieldSx}
                                    value={values['breakdown.totalTick'] ?? ''}
                                    onChange={(e) => onChange('breakdown.totalTick', e.target.value)}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack spacing={0.75} sx={{mb: 2, maxWidth: 720}}>
                <Typography variant="subtitle2" fontWeight={700}>{page.conclusionLabel}</Typography>
                <InputField
                    type="text"
                    fullWidth
                    compact
                    multiline
                    minRows={2}
                    placeholder={page.conclusionPlaceholder}
                    value={values.conclusion ?? ''}
                    onChange={(e) => onChange('conclusion', e.target.value)}
                />
            </Stack>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.5}}>
                {page.remarksTitle}
            </Typography>
            <Stack spacing={0.25}>
                {page.remarks.map((r, idx) => (
                    <Typography key={`${r.tick}-${idx}`} variant="body2" sx={{pl: 0.5, lineHeight: 1.4}}>
                        <Box component="span" sx={{display: 'inline-block', minWidth: 40, fontWeight: 600}}>
                            {r.tick}
                        </Box>
                        {r.text}
                    </Typography>
                ))}
            </Stack>
        </Box>
    );
}
